import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { Alert, Card, Center, Drawer, Group, Loader, Select, Stack, Text } from '@mantine/core';
import { IconInfoCircle } from '@tabler/icons-react';
import { EssaySummaryRow } from '../../hooks/useEssayApi';
import {
  useQuarterlyReport,
  useQuarterlySummary,
  useReturnQuarterly,
} from '../../hooks/useQuarterlyApi';
import { EssaySummaryTable } from '../../components/essays/EssaySummaryTable';
import { QuarterlyEditor } from '../../components/essays/QuarterlyEditor';
import { reportingQuarter } from '../../components/essays/quarterPeriod';
import { useDevice } from '../../hooks/useDevice';
import { LoadFailed } from '../../components/ui/LoadFailed';

/** Открытый из сводки отчёт — только на чтение: править чужое здесь нечего. */
const ManagedQuarterlyPanel: React.FC<{ reportId: string }> = ({ reportId }) => {
  const { t } = useTranslation();
  const { data, isLoading, isError } = useQuarterlyReport(reportId);
  const returnReport = useReturnQuarterly();

  if (isLoading) {
    return (
      <Center py="xl">
        <Loader />
      </Center>
    );
  }
  // Отчёт мог исчезнуть между загрузкой сводки и открытием панели.
  if (isError || !data) {
    return (
      <Alert icon={<IconInfoCircle size={16} />} color="gray" variant="light">
        {t('quarterly.noReport')}
      </Alert>
    );
  }

  return (
    <Stack gap="md">
      <Text c="dimmed" size="sm">
        {t('essays.readOnlyNotice')}
      </Text>
      <QuarterlyEditor
        key={data.report._id}
        report={data.report}
        readOnly
        canReturn={data.canReturn}
        isScored={data.isScored}
        isReturnPending={returnReport.isPending}
        onReturn={(comment) => returnReport.mutateAsync({ id: data.report._id, comment })}
      />
    </Stack>
  );
};

/**
 * Сводка квартальных отчётов для лида, PM и сквозного ревьюера.
 *
 * Строки строятся от людей, а не от заведённых отчётов: тот, у кого отчёта нет,
 * должен быть виден — иначе именно его и забудут.
 */
export const QuarterlyManage: React.FC = () => {
  const { t } = useTranslation();
  const isMobile = useDevice() === 'mobile';
  const [year, setYear] = useState(() => String(reportingQuarter().year));
  const [quarter, setQuarter] = useState(() => String(reportingQuarter().quarter));
  const [openedId, setOpenedId] = useState<string | null>(null);

  const {
    data: rows,
    isLoading,
    isError: isSummaryError,
  } = useQuarterlySummary(Number(year), Number(quarter), true);

  const quarterOptions = useMemo(
    () =>
      [1, 2, 3, 4].map((q) => ({ value: String(q), label: t('quarterly.quarterOption', { q }) })),
    [t],
  );
  const yearOptions = useMemo(() => {
    const y = dayjs().year();
    return Array.from({ length: 5 }, (_, i) => String(y - 3 + i));
  }, []);

  // Таблица людей у месячных и квартальных одна и та же; отличается только имя
  // поля с id документа.
  const tableRows: EssaySummaryRow[] = (rows || []).map((row) => ({
    userId: row.userId,
    name: row.name,
    position: row.position,
    essayId: row.reportId,
    status: row.status,
    updatedAt: row.updatedAt,
    submittedAt: row.submittedAt,
    returnedAt: row.returnedAt,
    isEmpty: row.isEmpty,
  }));

  const submittedCount = tableRows.filter((r) => r.status === 'submitted').length;

  return (
    <>
      <Card withBorder radius="md" padding="md" mb="md">
        <Group justify="space-between" align="flex-end" wrap="wrap">
          <Group wrap="wrap">
            <Select
              label={t('quarterly.quarter')}
              data={quarterOptions}
              value={quarter}
              onChange={(v) => v && setQuarter(v)}
              allowDeselect={false}
              comboboxProps={{ withinPortal: true }}
            />
            <Select
              label={t('essays.year')}
              data={yearOptions}
              value={year}
              onChange={(v) => v && setYear(v)}
              allowDeselect={false}
              comboboxProps={{ withinPortal: true }}
            />
          </Group>
          <Text c="dimmed" size="sm">
            {t('essays.summaryCount', { submitted: submittedCount, total: tableRows.length })}
          </Text>
        </Group>
      </Card>

      {isLoading ? (
        <Center py="xl">
          <Loader />
        </Center>
      ) : isSummaryError ? (
        <LoadFailed message={t('errors.loadFailed')} hasRetry />
      ) : tableRows.length === 0 ? (
        <Alert icon={<IconInfoCircle size={16} />} color="gray" variant="light">
          {t('essays.noPeopleForQuarter')}
        </Alert>
      ) : (
        <EssaySummaryTable
          rows={tableRows}
          selectedIds={new Set()}
          onToggle={() => undefined}
          onToggleAll={() => undefined}
          hasSelection={false}
          onOpen={(row) => row.essayId && setOpenedId(row.essayId)}
        />
      )}

      <Drawer
        opened={!!openedId}
        onClose={() => setOpenedId(null)}
        position={isMobile ? 'bottom' : 'right'}
        size={isMobile ? '90%' : 'xl'}
        title={t('quarterly.title')}
      >
        {openedId && <ManagedQuarterlyPanel reportId={openedId} />}
      </Drawer>
    </>
  );
};
